/**
 * HelpSearchView — champ UNIFIÉ du Help Drawer (HELP-DRAWER §3-§7 + lot §3/§4).
 *
 * Un seul champ réunit l'entrée « Demander à l'IA » (violette) et la recherche
 * locale instantanée d'articles. Deux usages (`variant`) :
 *   - `list`    : vue liste (champ vide → récents + articles de la page regroupés ;
 *                 rempli → résultats globaux à plat) ;
 *   - `article` : champ en TÊTE d'article (porte de sortie globale + entrée IA) —
 *                 champ vide n'affiche rien (l'article est sous le champ).
 *
 * Présélection pilotée par l'INTENTION (§4) : question → entrée IA présélectionnée ;
 * mot unique / lexical → premier article présélectionné (l'entrée IA reste visible
 * et cliquable, mais n'est plus déclenchée par Entrée).
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, Search as SearchIcon, Sparkles } from 'lucide-react'

import { EmptyStateCTA } from '../shared/EmptyStateCTA'
import { Search } from '../ui/search'
import { type HelpArticle } from '../../hooks/useHelp'
import { searchArticles, type FoldedHelpItem } from '../../lib/helpSearch'
import { isLikelyQuestion } from '../../lib/helpIntent'
import { AI_MIN_CHARS, MAX_SEARCH_RESULTS } from '../../lib/helpConstants'
import type { RecentEntry } from '../../lib/helpSearchHistory'
import { HelpAiEntry } from './HelpAiEntry'
import { HelpArticleResult } from './HelpArticleResult'
import { HelpContextualArticles } from './HelpContextualArticles'
import { HelpRecents } from './HelpRecents'

export interface HelpSearchViewProps {
    variant?: 'list' | 'article'
    query: string
    onQueryChange: (q: string) => void
    contextualArticles: HelpArticle[]
    indexItems: FoldedHelpItem[]
    indexLoading: boolean
    contextSlugs: Set<string>
    recents: RecentEntry[]
    enableAssistant: boolean
    onAskAI: (query: string) => void
    onOpenArticle: (slug: string) => void
    onReplaySearch: (query: string) => void
    onReopenConversation: (entry: Extract<RecentEntry, { kind: 'conversation' }>) => void
    onRemoveRecent: (entry: RecentEntry) => void
    onClearRecent: () => void
    inputRef: React.RefObject<HTMLInputElement>
}

type NavItem = { type: 'ai' } | { type: 'article'; slug: string }

export function HelpSearchView({
    variant = 'list',
    query,
    onQueryChange,
    contextualArticles,
    indexItems,
    indexLoading,
    contextSlugs,
    recents,
    enableAssistant,
    onAskAI,
    onOpenArticle,
    onReplaySearch,
    onReopenConversation,
    onRemoveRecent,
    onClearRecent,
    inputRef,
}: HelpSearchViewProps) {
    const { t } = useTranslation()
    const trimmed = query.trim()
    const isSearching = trimmed.length > 0
    // §4 + retour PO : l'IA n'est déclenchable que pour une vraie question (un mot
    // seul renverrait « je ne peux pas vous aider »). On ne MONTRE l'entrée IA que
    // dans ce cas (jamais grisée → pas d'effet « bug ») ; sinon, dès ≥ seuil, un
    // INDICE discret non cliquable enseigne comment l'utiliser (découvrabilité).
    const aiTriggerable =
        enableAssistant && trimmed.length >= AI_MIN_CHARS && isLikelyQuestion(trimmed)
    const aiHintVisible =
        enableAssistant && trimmed.length >= AI_MIN_CHARS && !aiTriggerable

    const results = useMemo(
        () =>
            isSearching
                ? searchArticles(query, indexItems, {
                      limit: MAX_SEARCH_RESULTS,
                      contextSlugs,
                  })
                : [],
        [query, isSearching, indexItems, contextSlugs],
    )

    const navItems = useMemo<NavItem[]>(() => {
        if (!isSearching) return []
        const items: NavItem[] = []
        if (aiTriggerable) items.push({ type: 'ai' })
        for (const r of results) items.push({ type: 'article', slug: r.item.slug })
        return items
    }, [isSearching, aiTriggerable, results])

    // §4 : l'index 0 EST l'élément présélectionné — l'IA (question, en tête) ou, à
    // défaut de carte IA, le premier article. Réinitialisé à chaque frappe.
    const [activeIndex, setActiveIndex] = useState(0)
    useEffect(() => {
        setActiveIndex(0)
    }, [query])

    const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
    useEffect(() => {
        itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex])

    const triggerAi = () => {
        if (aiTriggerable) onAskAI(trimmed)
    }
    const activate = (index: number) => {
        const item = navItems[index]
        if (!item) return
        if (item.type === 'ai') triggerAi()
        else onOpenArticle(item.slug)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            triggerAi() // ⌘/Ctrl+Entrée : déclenche TOUJOURS l'IA.
            return
        }
        const n = navItems.length
        if (e.key === 'ArrowDown') {
            if (n > 0) {
                e.preventDefault()
                setActiveIndex((i) => Math.min(i + 1, n - 1))
            }
        } else if (e.key === 'ArrowUp') {
            if (n > 0) {
                e.preventDefault()
                setActiveIndex((i) => Math.max(i - 1, 0))
            }
        } else if (e.key === 'Tab') {
            if (n === 0) return
            if (!e.shiftKey && activeIndex < n - 1) {
                e.preventDefault()
                setActiveIndex((i) => i + 1)
            } else if (e.shiftKey && activeIndex > 0) {
                e.preventDefault()
                setActiveIndex((i) => i - 1)
            }
        } else if (e.key === 'Enter') {
            if (n > 0) {
                e.preventDefault()
                activate(activeIndex)
            }
        } else if (e.key === 'Escape') {
            if (trimmed.length > 0) {
                e.preventDefault()
                e.stopPropagation()
                onQueryChange('')
            }
        }
    }

    let renderIndex = 0

    return (
        <div className="space-y-4">
            <Search
                ref={inputRef}
                value={query}
                onValueChange={onQueryChange}
                onClear={() => onQueryChange('')}
                onKeyDown={handleKeyDown}
                placeholder={t('help.search.placeholder')}
                clearLabel={t('common.clear', { defaultValue: 'Effacer' })}
                containerClassName="bg-card rounded-xl shadow-md"
                role="combobox"
                aria-expanded={navItems.length > 0}
                aria-label={t('help.search.placeholder')}
                autoComplete="off"
            />

            {isSearching ? (
                <div className="space-y-2">
                    {aiTriggerable ? (
                        (() => {
                            const idx = renderIndex++
                            return (
                                <HelpAiEntry
                                    query={trimmed}
                                    active={activeIndex === idx}
                                    onTrigger={triggerAi}
                                    onHover={() => setActiveIndex(idx)}
                                    buttonRef={(el) => (itemRefs.current[idx] = el)}
                                />
                            )
                        })()
                    ) : aiHintVisible ? (
                        // Indice discret NON cliquable (découvrabilité sans « bouton cassé »).
                        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
                            <Sparkles className="h-3.5 w-3.5 shrink-0 text-ai/70" />
                            {t('help.search.aiNeedsQuestion')}
                        </p>
                    ) : null}

                    {results.length > 0 ? (
                        <ul className="space-y-2">
                            {results.map((r) => {
                                const idx = renderIndex++
                                return (
                                    <li key={r.item.slug}>
                                        <HelpArticleResult
                                            title={r.item.title}
                                            query={query}
                                            onThisPage={r.onThisPage}
                                            active={activeIndex === idx}
                                            onOpen={() => onOpenArticle(r.item.slug)}
                                            onHover={() => setActiveIndex(idx)}
                                            buttonRef={(el) => (itemRefs.current[idx] = el)}
                                        />
                                    </li>
                                )
                            })}
                        </ul>
                    ) : (
                        !indexLoading && (
                            <div className="rounded-xl bg-card shadow-md">
                                <EmptyStateCTA
                                    icon={SearchIcon}
                                    title={t('help.search.noResults')}
                                    description={enableAssistant
                                        ? t('help.search.noResultsHintAi')
                                        : t('help.search.noResultsHint')}
                                    className="px-4 py-6"
                                />
                            </div>
                        )
                    )}
                </div>
            ) : variant === 'article' ? null : (
                <>
                    <HelpRecents
                        entries={recents}
                        onReplaySearch={onReplaySearch}
                        onReopenConversation={onReopenConversation}
                        onRemove={onRemoveRecent}
                        onClear={onClearRecent}
                    />
                    {contextualArticles.length > 0 ? (
                        <HelpContextualArticles
                            articles={contextualArticles}
                            onSelectArticle={onOpenArticle}
                        />
                    ) : (
                        <div className="rounded-xl bg-card shadow-md">
                            <EmptyStateCTA
                                icon={BookOpen}
                                title={t('help.noContextualArticles')}
                                description={t('help.search.emptyHint')}
                                className="px-4 py-8"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
